import React, { FC, useEffect, useState, useRef, Suspense, lazy } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router';
import {
    buildPlayerRoutes,
    buildSectionDetailRouting,
    buildSectionCollectionRouting,
    buildNavigationRoutes,
    buildExpandablePageRouting,
    buildLanguageSettingsRoutes,
} from 'utils/fnRouting';
import { SECONDS } from 'utils/TimeUnit';
import { useChannels } from 'providers/useChannels/useChannels';
import useYouboraNavigation from 'hooks/useYouboraNavigation';
import { useNavigationData } from 'providers/useNavigation/NavigationContext';
import { NavigationAllLanguages } from 'types/Navigation';
import YouboraTracking from 'providers/player/YouboraTracking';
import ApplicationConfig from 'providers/useConfig/ApplicationConfig';
import { useConfig } from 'providers/useConfig/ConfigContext';
import { GuestAlert, InhibitionAlert, InhibitionPlayerRouteGuard, SignOut, SignedOutNotice, useAuth } from 'providers/useAuth/AuthContext';
import { usePurchase, usePurchaseFlowScreenProvider } from 'providers/usePurchase/PurchaseContext';
import { useUpsellFlow, useUpsellFlowScreenProvider } from 'providers/useUpsellFlow/UpsellFlowContext';
import { useApp, useAppHistory } from 'providers/useApp/AppContext';
import { StaticPageRoutes } from 'types/RouteTypes';
import { PurchasePinDialog } from 'providers/usePin/PinContext';
import { useSubscribeFlowScreenProvider, useSubscription } from 'providers/useSubscription/SubscriptionContext';
import { useUILanguageChangedObserver } from 'hooks/useUILanguageChangedObserver/useUILanguageChangedObserver';
import { useServiceActivationWatcher } from 'hooks/useServiceActivationWatcher/useServiceActivationWatcher';
import {
    CreateRecordingError,
    DeleteRecordingConfirmation,
    ManageRecordingLoadingOverlay,
    useRecording,
} from 'providers/useRecording/RecordingContext';
import translate from 'utils/fnTranslate';
import { takeFlowAfterNavigation } from 'utils/fnPurchaseFlow';
import { LocalStorage } from 'types/Storage';
import { getLocalStorage, setLocalStorage } from 'utils/fnStorage';
import { HintModal } from 'providers/useHint/HintContext';
import { checkUserSubscription } from 'providers/useAuth/AuthService';
import { usePlayer } from 'providers/player/PlayerContext';
import { playerRouteMatch } from 'hooks/usePlayerRouter';
import { PlayOptionSelector } from './PlayOptionScreen/PlayOptionScreen';
import { CastMiniController } from './CastMiniController/CastMiniController';
import { Player } from './Player/Player';
import { DeleteAllRecordingsConfirmation } from './Pages/Settings/PageRecordings/PageRecordingsSettings';
import { SmartAppBanner } from './SmartAppBanner/SmartAppBanner';
import { MaintenanceAlert } from './Maintenance/MaintenanceAlert';
import PagePinProtection from './Pages/Settings/PagePinProtection/PagePinProtection';
import PageAccessibility from './Pages/Settings/PageAccessibility/PageAccessibility';
import PageAccessibilitySubtitles from './Pages/Settings/PageAccessibility/PageAccessibilitySubtitles';
import { SplashScreen } from './SplashScreen/SplashScreen';
import { ConfigErrorScreen } from './ConfigErrorScreen/ConfigErrorScreen';
import { PageLayout } from './PageLayout/PageLayout';
import PageTakeover from './Pages/Settings/PageTakeover/PageTakeover';
import { CookieNotification } from './CookieNotification/CookieNotification';
import { styled } from './ThemeConfigConnector/ThemeConfigConnnector';
import { SubscriptionErrorScreen } from './SubscriptionErrorScreen/SubscriptionErrorScreen';
import PageAccessibilityNotice from './Pages/Settings/PageAccessibility/PageAccessibilityNotice';

const CONFIG_UPDATE_APPLIED = 'CONFIG_UPDATE_APPLIED';

const PageDefault = lazy(() => import('./Pages/PageDefault/PageDefault'));
const PageSettings = lazy(() => import('./Pages/Settings/PageSettings/PageSettings'));
const PageAbout = lazy(() => import('./Pages/Settings/PageAbout/PageAbout'));
const PageRecordingsSettings = lazy(() => import('./Pages/Settings/PageRecordings/PageRecordingsSettings'));
const PageAutoPlay = lazy(() => import('./Pages/Settings/PageAutoPlay/PageAutoPlay'));
const PageDeviceManager = lazy(() => import('./Pages/PageDeviceManager/PageDeviceManager'));
const PageSubscription = lazy(() => import('./Pages/Settings/PageSubscription/PageSubscription'));
const PageAccountSettings = lazy(() => import('./Pages/Settings/PageSettings/AccountSettings'));

export const SETTINGS_PIN = 'SETTINGS_PIN';
export const SETTINGS_RECORDINGS = 'SETTINGS_RECORDINGS';
export const SETTINGS_AUTOPLAY = 'SETTINGS_AUTOPLAY';
export const SETTINGS_ABOUT_APP = 'SETTINGS_ABOUT_APP';
export const SETTINGS_LANGUAGE = 'SETTINGS_LANGUAGE';
export const SETTINGS_DEVICE_MANAGEMENT = 'SETTINGS_DEVICE_MANAGEMENT';
export const SETTINGS_ACCOUNT = 'SETTINGS_ACCOUNT';
export const SETTINGS_ACCESSIBILITY = 'SETTINGS_ACCESSIBILITY';
export const SETTINGS_TAKEOVER = 'SETTINGS_TAKEOVER';

const RentSubscribeScreenProvider: FC = () => {
    const { cancel: exitRentingFlow } = usePurchase();
    const { cancel: exitSubscribeFlow } = useSubscription();
    const { cancel: exitUpsellFlow } = useUpsellFlow();
    const { pathname } = useLocation();

    const rentingScreen = usePurchaseFlowScreenProvider();
    const subscribeScreen = useSubscribeFlowScreenProvider();
    const upsellScreen = useUpsellFlowScreenProvider();

    useEffect(() => {
        takeFlowAfterNavigation()?.();

        return () => {
            exitSubscribeFlow();
            exitRentingFlow();
            exitUpsellFlow();
        };
    }, [pathname]);

    return (
        <React.Fragment>
            {rentingScreen()}
            {subscribeScreen()}
            {upsellScreen()}
        </React.Fragment>
    );
};

const NavigationContainer = styled.div<{ blur?: boolean; isSmartBannerOpen: boolean }>`
    filter: ${(props) => (props.blur ? 'blur(10px)' : 'none')};
    padding-top: ${(props) => (props.isSmartBannerOpen ? '80' : '0')}px;
`;

const BuildPageRoutes: React.FC<{
    navigationData: NavigationAllLanguages;
    isSmartBannerOpen: boolean;
}> = ({ navigationData, isSmartBannerOpen }) => {
    const location = useLocation();
    const { authApiInitialized, isLoggedIn, isGuest } = useAuth();
    const { appLanguage } = useApp();

    const enableRoutes = authApiInitialized && isLoggedIn;

    useEffect(() => {
        if (appLanguage) {
            document.documentElement.lang = appLanguage;
        }
    }, [appLanguage]);

    // We can probably remove this before prod, but right now sometimes the navigation isnt loaded in properly which creates some interesting issues
    const navigationRoutesReady = Object.values(navigationData ?? {}).some((nav) => nav?.menuItems?.length);

    useEffect(() => {
        if (getLocalStorage(CONFIG_UPDATE_APPLIED as keyof LocalStorage) === false) {
            setLocalStorage(CONFIG_UPDATE_APPLIED as keyof LocalStorage, true);
            window.location.reload();
        }
    }, [location]);

    return (
        <Suspense fallback={null}>
            <Routes>
                {/* Pages without header / footer / page layout */}

                {/* Player */}
                {buildPlayerRoutes(enableRoutes)}

                {enableRoutes && (
                    <Route
                        path="*"
                        element={
                            <PageLayout pathname={location.pathname}>
                                <NavigationContainer isSmartBannerOpen={isSmartBannerOpen}>
                                    <Routes>
                                        {buildSectionDetailRouting(appLanguage)}

                                        {buildSectionCollectionRouting()}

                                        {buildNavigationRoutes(navigationData)}

                                        {buildExpandablePageRouting()}

                                        {isLoggedIn && !isGuest && buildLanguageSettingsRoutes()}

                                        {<Route element={<PageAccessibilityNotice />} path={StaticPageRoutes.ACCESSIBILITY_NOTICE} />}

                                        {/* PIN protection? Language preferences? */}
                                        {isLoggedIn && !isGuest && <Route element={<PageSettings />} path={StaticPageRoutes.SETTINGS} />}
                                        {isLoggedIn && !isGuest && (
                                            <Route element={<PageRecordingsSettings />} path={StaticPageRoutes.RECORDINGS_SETTINGS} />
                                        )}
                                        {isLoggedIn && !isGuest && (
                                            <Route element={<PagePinProtection />} path={StaticPageRoutes.PIN_PROTECTION} />
                                        )}
                                        {isLoggedIn && !isGuest && <Route element={<PageAutoPlay />} path={StaticPageRoutes.AUTOPLAY} />}
                                        {isLoggedIn && !isGuest && (
                                            <Route element={<PageDeviceManager />} path={StaticPageRoutes.DEVICE_MANAGER} />
                                        )}
                                        {isLoggedIn && !isGuest && <Route element={<PageAbout />} path={StaticPageRoutes.ABOUT} />}
                                        {isLoggedIn && !isGuest && (
                                            <Route element={<PageAccessibility />} path={StaticPageRoutes.ACCESSIBILITY} />
                                        )}
                                        {isLoggedIn && !isGuest && (
                                            <Route
                                                element={<PageAccessibilitySubtitles />}
                                                path={StaticPageRoutes.ACCESSIBILITY_SUBTITLES}
                                            />
                                        )}
                                        {isLoggedIn && !isGuest && <Route element={<PageTakeover />} path={StaticPageRoutes.TAKEOVER} />}
                                        {isLoggedIn && !isGuest && (
                                            <Route element={<PageSubscription />} path={StaticPageRoutes.SUBSCRIPTION} />
                                        )}
                                        {isLoggedIn && !isGuest && (
                                            <Route element={<PageAccountSettings />} path={StaticPageRoutes.ACCOUNT} />
                                        )}

                                        {navigationRoutesReady && <Route element={<PageDefault />} path="*" />}
                                    </Routes>
                                </NavigationContainer>
                            </PageLayout>
                        }
                    />
                )}
            </Routes>
        </Suspense>
    );
};

const SignedOutPlayerRouteGuard: FC = () => {
    const { isSignedOutNotice } = useAuth();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { resetPlayerAsset, isMini, closeMiniPlayer } = usePlayer();

    useEffect(() => {
        if (!isSignedOutNotice) return;

        if (isMini) {
            closeMiniPlayer();
        }

        if (playerRouteMatch(pathname).type) {
            resetPlayerAsset();
            navigate('/', { replace: true });
        }
    }, [isSignedOutNotice, pathname]);

    return null;
};

export const Application: React.FC = () => {
    const { config, translations, configError, configRetrying, retryConfig } = useConfig();
    const { loadNavigationData, getNavigationDataAllLanguages } = useNavigationData();
    const { isLoggedIn, authApiInitialized, closeGuestAlert, closeLogOutAlert, logUserOut, isGuest, userInfoDetails } = useAuth();
    const { resetApi } = usePurchase();
    const { fetchChannels } = useChannels();
    const [isAppBooted, setIsAppBooted] = useState(false);
    const { setToDelete, setError } = useRecording();
    const [isSmartBannerOpen, setIsSmartBannerOpen] = useState<boolean>(false);
    const [isMaintenanceActive, setIsMaintenanceActive] = useState<boolean>(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [hasSubscription, setHasSubscription] = useState(false);
    const maintenanceStart = useRef(null);
    const maintenanceEnd = useRef(null);
    const maintenanceTimeout = useRef(null);
    const youboraNavRef = useRef<{ unlisten: (() => void) | null }>({ unlisten: null });
    const youboraNav = useYouboraNavigation();
    const channelsFetched = useRef(false);

    useUILanguageChangedObserver();
    useAppHistory();
    useServiceActivationWatcher();

    const accessGranted = authApiInitialized && isLoggedIn;

    const closeAlerts = () => {
        closeGuestAlert();
        closeLogOutAlert();
        resetApi();
        setError(null);
        setToDelete(null);
    };

    useEffect(() => {
        window.onpopstate = closeAlerts;
    }, []);

    const checkSubscription = async () => {
        setSubscriptionStatus('loading');

        const result = await checkUserSubscription();

        if (result.error) {
            setSubscriptionStatus('error');
        } else {
            setHasSubscription(result.hasSubscription);
            setSubscriptionStatus('success');
        }
    };

    useEffect(() => {
        if (isAppBooted && authApiInitialized && isLoggedIn) {
            checkSubscription();
        } else if (isAppBooted && authApiInitialized && !isLoggedIn) {
            setSubscriptionStatus('success');
            setHasSubscription(false);
        }
    }, [isAppBooted, authApiInitialized, isLoggedIn]);

    useEffect(() => {
        if (config && translations) {
            maintenanceStart.current = SECONDS.toMillis(config?.api_config?.maintenance_mode?.start);
            maintenanceEnd.current = SECONDS.toMillis(config?.api_config?.maintenance_mode?.end);
            const now = Date.now();

            if (maintenanceStart.current && maintenanceEnd.current && !isMaintenanceActive) {
                if (maintenanceStart.current > now && now < maintenanceEnd.current) {
                    if (maintenanceTimeout.current) {
                        clearTimeout(maintenanceTimeout.current);
                    }
                    maintenanceTimeout.current = window.setTimeout(() => {
                        if (isLoggedIn) {
                            logUserOut(false, true);
                        }
                        setIsMaintenanceActive(true);
                    }, maintenanceStart.current - now);
                } else if (maintenanceStart.current < now && now < maintenanceEnd.current) {
                    if (isLoggedIn) {
                        logUserOut(false, true);
                    }
                    setIsMaintenanceActive(true);
                }
            }
        }
    }, [config, translations, isLoggedIn]);

    useEffect(() => {
        if (!isMaintenanceActive && authApiInitialized) {
            if (config && translations) {
                loadNavigationData();
            }

            if (config && translations && !channelsFetched.current) {
                channelsFetched.current = true;
                fetchChannels();
            }

            if (config && translations && accessGranted) {
                setIsAppBooted(true);
            }
        }
    }, [config, translations, isLoggedIn, authApiInitialized, isMaintenanceActive]);

    useEffect(() => {
        if (isAppBooted && (userInfoDetails?.subscription || isGuest)) {
            const youboraTracking = YouboraTracking.getInstance();
            const trackingUserId = isGuest ? 'kylaline' : userInfoDetails.subscription;

            if (!youboraTracking.isInitialized) {
                try {
                    const initOptions = {
                        appName: 'Elisa Elamus WEB',
                        // eslint-disable-next-line no-underscore-dangle
                        appVersion: window.__VERSION__,
                        userId: trackingUserId,
                    };

                    youboraTracking.initializePlugin(ApplicationConfig?.app_config?.miscellanious?.youbora_account_code, initOptions);
                } catch (error) {
                    console.error('Application: Failed to initialize YouboraTracking:', error);
                }
            }

            if (isLoggedIn && !isGuest && userInfoDetails?.email) {
                youboraTracking.handleLoginStateChange(true, userInfoDetails.subscription, true);
            }
        }
    }, [isAppBooted, userInfoDetails, isLoggedIn, isGuest]);

    useEffect(() => {
        if (isAppBooted && !youboraNavRef.current.unlisten) {
            youboraNavRef.current.unlisten = youboraNav.startTracking();
        }

        return () => {
            if (youboraNavRef.current.unlisten) {
                youboraNavRef.current.unlisten();
                youboraNavRef.current.unlisten = null;
            }
        };
    }, [isAppBooted, youboraNav]);

    useEffect(() => {
        if (isAppBooted) {
            const handleBeforeUnload = () => {
                const youboraTracking = YouboraTracking.getInstance();
                youboraTracking.handleAppExit();
            };

            window.addEventListener('beforeunload', handleBeforeUnload);

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
            };
        }

        return undefined;
    }, [isAppBooted]);

    if (isMaintenanceActive) {
        return (
            <MaintenanceAlert
                startTime={maintenanceStart.current}
                endTime={maintenanceEnd.current}
                hideMaintenance={() => {
                    setIsMaintenanceActive(false);
                }}
            />
        );
    }

    // only when the app never got a usable config
    if (configError && !config) {
        return <ConfigErrorScreen onRetry={retryConfig} isRetrying={configRetrying} />;
    }

    if (!isAppBooted || !authApiInitialized || subscriptionStatus === 'loading') {
        return <SplashScreen />;
    }

    if (isLoggedIn && !isGuest && (subscriptionStatus === 'error' || !hasSubscription)) {
        return <SubscriptionErrorScreen />;
    }

    return (
        <>
            <BuildPageRoutes navigationData={getNavigationDataAllLanguages()} isSmartBannerOpen={isSmartBannerOpen} />
            {/* Helps tab navigation.. _sometimes_ */}
            <CookieNotification />

            <Player />

            <CastMiniController />

            {/* Notifications */}
            <RentSubscribeScreenProvider />

            {!isGuest && <HintModal />}

            <PurchasePinDialog />
            <PlayOptionSelector />

            <DeleteRecordingConfirmation />
            <DeleteAllRecordingsConfirmation />
            <ManageRecordingLoadingOverlay />
            <CreateRecordingError />

            <GuestAlert />
            <SignOut />
            <SignedOutNotice />
            <InhibitionAlert />
            <InhibitionPlayerRouteGuard />
            <SignedOutPlayerRouteGuard />

            <SmartAppBanner
                name={translate('APP_BANNER_TITLE')}
                hintText={translate('APP_BANNER_HINT')}
                onOpen={() => setIsSmartBannerOpen(true)}
                onClose={() => setIsSmartBannerOpen(false)}
                viewButton={translate('APP_BANNER_VIEW_BUTTON')}
                androidAppId={config?.app_config.smart_app_banner.android_app_id}
                iOsAppId={config?.app_config.smart_app_banner.ios_app_id}
            />
        </>
    );
};
export default Application;
